import {
  useHasMoreFileMessages,
  useOldestFileMessages,
} from '../store/useMessageHooks';
import { useCallback } from 'react';
import {
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { Message, MessageType } from '../../type';
import { useMessageCollection } from '../../../../firebase/useCollection';
import { useCareTeam } from '../store/useCareTeam';
import { useStorageInfoFetcher } from './useStorageInfoFetcher';
import { FileUploadType } from '../../store/types';
import { filterLast240Days } from './common';

export const useFileMessageLoader = (
  channelGuid: string,
  fileUploadType: FileUploadType,
): (() => Promise<boolean>) => {
  const [, dispatch] = useCareTeam();
  const collection = useMessageCollection(channelGuid);
  const enrichStorageInfo = useStorageInfoFetcher();
  const message = useOldestFileMessages(channelGuid, fileUploadType);
  const currentHasMore = useHasMoreFileMessages(channelGuid, fileUploadType);
  const pageSize = 20;
  return useCallback(async () => {
    if (!currentHasMore) {
      return false;
    }
    const messageTypes: MessageType[] =
      fileUploadType === 'MEDIA' ? ['IMAGE', 'VIDEO'] : ['PDF', 'WORD'];
    const messageQuery = query(
      collection,
      where('messageType', 'in', messageTypes),
      where('scanStatus', '==', 'CLEAN'),
      orderBy('createdDate', 'desc'),
      orderBy('authorGuid', 'asc'),
      startAfter(message?.createdDate.toDate() ?? new Date()),
      limit(pageSize),
    );
    const snapshot = await getDocs(messageQuery);
    const messages = snapshot.docs.map((doc) => doc.data() as Message);
    const messagesInLast240Days = filterLast240Days(messages);
    const storageInfoEnrichedMessages = await enrichStorageInfo(
      messagesInLast240Days,
    );
    const hasMore = messagesInLast240Days.length === pageSize;
    dispatch({
      type: 'FILE_MESSAGE_LOADED',
      fileUploadType,
      channelGuid,
      messages: storageInfoEnrichedMessages,
      hasMore,
    });
    return hasMore;
  }, [
    channelGuid,
    fileUploadType,
    collection,
    message,
    currentHasMore,
    dispatch,
    enrichStorageInfo,
  ]);
};
